import type { ReactNode } from "react";
import { Button, type ButtonProps } from "@heroui/react";

type K1ButtonProps = Omit<ButtonProps, "children"> & {
  children: ReactNode;
};

export function K1PrimaryButton({
  className,
  children,
  ...props
}: K1ButtonProps) {
  return (
    <Button
      {...props}
      className={[
        "h-10 rounded-full !bg-blue-600 px-4 text-sm font-medium !text-white hover:!bg-blue-700",
        "data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-blue-200",
        "shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_1px_2px_rgba(15,23,42,0.08)]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </Button>
  );
}

export function K1SecondaryButton({
  className,
  children,
  ...props
}: K1ButtonProps) {
  return (
    <Button
      {...props}
      variant="bordered"
      className={[
        "h-10 rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50",
        "data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-blue-200",
        "shadow-[0_1px_0_rgba(255,255,255,0.6)_inset]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </Button>
  );
}

export function K1TertiaryButton({
  className,
  children,
  ...props
}: K1ButtonProps) {
  return (
    <Button
      {...props}
      variant="light"
      className={[
        "h-9 rounded-full px-3 text-sm font-medium text-slate-600 hover:bg-slate-100",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </Button>
  );
}

export function K1IconButton({ className, children, ...props }: K1ButtonProps) {
  return (
    <Button
      {...props}
      isIconOnly
      variant="light"
      className={[
        "h-9 w-9 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700",
        "data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-blue-200",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </Button>
  );
}

type K1FundsButtonProps = Omit<ButtonProps, "children"> & {
  isActive?: boolean;
};

export function K1FundsButton({
  className,
  isActive = false,
  ...props
}: K1FundsButtonProps) {
  return (
    <Button
      {...props}
      variant="bordered"
      className={[
        "h-10 rounded-full border px-4 text-sm font-medium",
        "data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-blue-200",
        "shadow-[0_1px_0_rgba(255,255,255,0.6)_inset]",
        isActive
          ? "border-blue-200 bg-blue-50/60 text-blue-700 hover:bg-blue-50"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span>Funds</span>
    </Button>
  );
}
